// ProjectRecap.js — Ergonomic recap screen (Bootstrap Icons only) + guided sections + safe media gallery
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './ProjectRecap.css';

import { QRCodeCanvas } from 'qrcode.react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthContext';

/* ============================================================================
 * I18N (same logic as LandingPage.js)
 * - Language is set in SettingsPage and stored in localStorage("weavevo_lang")
 * - Fallback: localStorage("weavevo_language")
 * ========================================================================== */

const PR_LANG_KEY = "weavevo_lang";
const PR_LANG_FALLBACK_KEY = "weavevo_language";

function safeReadLang(storageKey = PR_LANG_KEY) {
  try {
    const raw =
      localStorage.getItem(storageKey) ||
      localStorage.getItem(PR_LANG_FALLBACK_KEY) ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

function safeEnsureLang(next, storageKey = PR_LANG_KEY) {
  try {
    const raw =
      localStorage.getItem(storageKey) ||
      localStorage.getItem(PR_LANG_FALLBACK_KEY) ||
      "";
    if (!raw) localStorage.setItem(storageKey, next);
  } catch {}
}

const PR_I18N = {
  en: {
    common_share: "Share",
    common_show: "Show",
    common_hide: "Hide",
    common_send: "Send",
    // header / nav
    pr_closed: "Closed",
    pr_subtitle: "Project recap, contributions and final video.",
    pr_contributors: "Contributors",
    pr_forum_messages: "Forum messages",
    pr_scenario_steps: "Scenario steps",
    pr_dashboard: "Dashboard",

    // final video
    pr_watch: "Watch",
    pr_no_final: "No final video available",
    pr_come_back: "Come back after the montage is generated.",
    pr_copy_link: "Copy link",
    pr_download: "Download",
    pr_open_tab: "Open in a tab",
    pr_open: "Open",
    pr_link_copied: "Link copied",
    pr_final_of_project: "project final",
    pr_final_desc: "A final video assembled from the team’s contributions.",

    // youtube / diffusion
        pr_publish: "Publish",
        pr_youtube_section_title: "Distribution",
        pr_youtube_sub_publish: "Publish to YouTube",
        pr_youtube_sub_published: "YouTube & stats",
        pr_youtube_connected: "Connected",
        pr_youtube_not_connected: "Not connected",
        pr_youtube_connect: "Connect YouTube",
        pr_youtube_disconnect: "Disconnect",
        pr_youtube_connecting: "Connecting…",
        pr_youtube_disconnecting: "Disconnecting…",
        pr_youtube_connect_first: "Please connect your YouTube account first.",
        pr_publishing: "Publishing…",
    pr_title: "Title",
    pr_description: "Description",
    pr_publish_tip:
      "Tip: keep the title short, and put credits at the beginning of the description.",
    pr_views: "Views",
    pr_likes: "Likes",
    pr_comments_short: "Com.",
    pr_realtime_views: "Real-time views",
    pr_likes_demo: "Likes (demo)",
    pr_comments_demo: "Comments (demo)",
    pr_revenue_est: "Estimated revenue (simulation):",
    pr_revenue_note:
      "Indicative estimate. Monetization & Partner Program required.",
    pr_publish_details: "Publishing details",

    // sections / tooltips
    pr_final_video: "Final video",
    pr_distribution: "Distribution",
    pr_forum: "Forum",
    pr_gallery: "Contribution gallery",
    pr_participants: "Participants",
    pr_more: "More",
    pr_actions_video: "Video actions",
    pr_video_menu: "Video menu",
    pr_project_nav: "Project navigation",
    pr_watch_or_share: "Watch or share",
    pr_open_final_video: "Open final video",
    pr_link_qr: "Direct link + QR code",
    pr_filter_hint:
      "Filter by contributor and type, open full screen",

    // comments / search
    pr_add_comment_ph: "Add a comment…",
    pr_search_forum: "Search in the forum",
    pr_search_forum_ph: "Search a message or a contributor…",
    pr_clear_search: "Clear search",
    pr_erase: "Clear",
    pr_go_last: "Go to latest messages",
    pr_you: "You",
    pr_all_contributors: "All contributors",
    pr_back_dashboard: "Back to dashboard",

    // toasts
    toast_comment_added: "Comment added!",
    pr_no_comment_title: "No comment yet",
    pr_no_comment_desc: "Be the first to react to the final video.",
    pr_err_invalid_response: "Invalid response from the server.",
    pr_err_like: "Error while liking.",
    pr_err_send: "Error while sending.",
    toast_login_like_video: "Please log in to like this video.",
    toast_login_comment: "Please log in to comment.",
  },
  fr: {
    common_share: "Partager",
    common_show: "Afficher",
    common_hide: "Masquer",
    common_send: "Envoyer",
    // header / nav
    pr_closed: "Clôturé",
    pr_subtitle: "Récapitulatif du projet, contributions et vidéo finale.",
    pr_contributors: "Contributeurs",
    pr_forum_messages: "Messages forum",
    pr_scenario_steps: "Étapes scénario",
    pr_dashboard: "Tableau de bord",

    // final video
    pr_watch: "Visionner",
    pr_no_final: "Aucune vidéo finale disponible",
    pr_come_back: "Revenez après la génération du montage.",
    pr_copy_link: "Copier le lien",
    pr_download: "Télécharger",
    pr_open_tab: "Ouvrir dans un onglet",
    pr_open: "Ouvrir",
    pr_link_copied: "Lien copié",
    pr_final_of_project: "final du projet",
    pr_final_desc:
      "Une vidéo finale assemblée à partir des contributions de l’équipe.",

    // youtube / diffusion
        pr_publish: "Publier",
        pr_youtube_section_title: "Diffusion",
        pr_youtube_sub_publish: "Publier sur YouTube",
        pr_youtube_sub_published: "YouTube & statistiques",
        pr_youtube_connected: "Connecté",
        pr_youtube_not_connected: "Non connecté",
        pr_youtube_connect: "Connecter YouTube",
        pr_youtube_disconnect: "Déconnecter",
        pr_youtube_connecting: "Connexion…",
        pr_youtube_disconnecting: "Déconnexion…",
        pr_youtube_connect_first: "Veuillez d’abord connecter votre compte YouTube.",
        pr_publishing: "Publication…",
    pr_title: "Titre",
    pr_description: "Description",
    pr_publish_tip:
      "Conseil : gardez un titre court, et mettez les crédits en début de description.",
    pr_views: "Vues",
    pr_likes: "Likes",
    pr_comments_short: "Com.",
    pr_realtime_views: "Vues en temps réel",
    pr_likes_demo: "Likes (demo)",
    pr_comments_demo: "Commentaires (demo)",
    pr_revenue_est: "Revenu estimé (simulation) :",
    pr_revenue_note:
      "Estimation indicative. Monétisation & Programme Partenaire requis.",
    pr_publish_details: "Détails de publication",

    // sections / tooltips
    pr_final_video: "Vidéo finale",
    pr_distribution: "Diffusion",
    pr_forum: "Forum",
    pr_gallery: "Galerie des contributions",
    pr_participants: "Participants",
    pr_more: "Plus",
    pr_actions_video: "Actions vidéo",
    pr_video_menu: "Menu vidéo",
    pr_project_nav: "Navigation projet",
    pr_watch_or_share: "Regarder ou partager",
    pr_open_final_video: "Ouvrir la vidéo finale",
    pr_link_qr: "Lien direct + QR code",
    pr_filter_hint:
      "Filtrer par contributeur et type, ouvrir en plein écran",

    // comments / search
    pr_add_comment_ph: "Ajouter un commentaire…",
    pr_search_forum: "Rechercher dans le forum",
    pr_search_forum_ph: "Rechercher un message ou un contributeur…",
    pr_clear_search: "Effacer la recherche",
    pr_erase: "Effacer",
    pr_go_last: "Aller aux derniers messages",
    pr_you: "Vous",
    pr_all_contributors: "Tous les contributeurs",
    pr_back_dashboard: "Retour au tableau de bord",

    // toasts
    toast_comment_added: "Commentaire ajouté !",
    pr_no_comment_title: "Aucun commentaire",
    pr_no_comment_desc: "Soyez le premier à réagir à la vidéo finale.",
    pr_err_invalid_response: "Réponse invalide du serveur.",
    pr_err_like: "Erreur lors du like.",
    pr_err_send: "Erreur lors de l’envoi.",
    toast_login_like_video: "Connectez-vous pour aimer cette vidéo.",
    toast_login_comment: "Connectez-vous pour commenter.",
  },
};

function useWeavevoI18n(dict, storageKey = PR_LANG_KEY) {
  const location = useLocation();
  const [lang, setLang] = useState(() => safeReadLang(storageKey));
useEffect(() => {
  const read = () => {
    const next = safeReadLang(storageKey);
    setLang(next);
    safeEnsureLang(next, storageKey);
  };

  read();
  window.addEventListener("storage", read);
  window.addEventListener("focus", read);
  document.addEventListener("visibilitychange", read);

  return () => {
    window.removeEventListener("storage", read);
    window.removeEventListener("focus", read);
    document.removeEventListener("visibilitychange", read);
  };
}, [storageKey]);

// Re-read language on navigation (Settings page updates localStorage in the same tab)
useEffect(() => {
  const next = safeReadLang(storageKey);
  if (next !== lang) setLang(next);
}, [location.pathname, location.search, lang, storageKey]);

useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {}
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const dictForLang = dict?.[lang] || dict?.en || {};
      let out = dictForLang[key] ?? dict?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang, dict]
  );

  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const tt = useCallback((fr, en) => (lang === "fr" ? fr : en), [lang]);

  return { lang, locale, t, tt };
}

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const isVideoPath = (p = '') => /\.(mp4|webm|ogg)$/i.test(p);
const isImagePath = (p = '') => /\.(jpeg|jpg|png|gif|webp)$/i.test(p);

const safeJsonParse = (maybeJson, fallback) => {
  try {
    return JSON.parse(maybeJson);
  } catch {
    return fallback;
  }
};

const firstVal = (v) => (Array.isArray(v) ? v[0] : v);

const normalizeUploadsUrl = (raw) => {
  const v = firstVal(raw);
  if (!v) return '';
  const s = String(v).trim();
  if (!s) return '';
  if (s.startsWith('http') || s.startsWith('data:')) return s;
  if (s.startsWith('//')) return `https:${s}`;
  if (s.startsWith('/')) return s;
  // Avoid double-prefix when backend already returns an "uploads/..." path
  if (s.startsWith('uploads/')) return `/${s}`;
  if (s.startsWith('public/')) return `/${s}`;
  return `/uploads/${s.replace(/^\/+/, '')}`;
};


const formatCompact = (n) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return '0';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
};

const strHue = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
};

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '•';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name, size = 34, className = '' }) => {
  // Lightweight fallback avatar (transparent, no colored background)
  // Uses a stable hue so different users are still distinguishable.
  const hue = useMemo(() => strHue(name), [name]);
  const border = `1px solid hsla(${hue}, 45%, 40%, .22)`;
  const ink = `hsla(${hue}, 35%, 30%, .92)`;

  return (
    <div
      className={`wv-avatar ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        display: 'grid',
        placeItems: 'center',
        color: ink,
        fontSize: Math.max(12, Math.round(size * 0.34)),
        background: 'transparent',
        border,
        userSelect: 'none'
      }}
      title={name || ''}
      aria-label={name || 'Utilisateur'}
    >
      {initials(name)}
    </div>
  );
};

const ImgAvatar = ({ src, label, size = 34, className = '', style = {} }) => (
  <img
    src={src}
    alt={label || 'user'}
    className={`wv-imgAvatar ${className}`}
    style={{
      width: size,
      height: size,
      borderRadius: 999,
      objectFit: 'cover',
      background: 'transparent',
      border: '1px solid rgba(15,23,42,.14)',
      boxShadow: 'none',
      ...style
    }}
    loading="lazy"
    onError={(e) => {
      const seed = encodeURIComponent(label || 'user');
      const fallback = `https://api.dicebear.com/7.x/initials/svg?seed=${seed}`;
      if (e?.currentTarget && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
    }}
  />
);

const SectionShell = ({
  id,
  icon,
  title,
  subtitle,
  right,
  children,
  sectionClassName = '',
  cardClassName = '',
  cardStyle,
  bodyClassName = 'p-3 p-md-4',
}) => (
  <section id={id} className={`wv-section ${sectionClassName}`.trim()} aria-label={title}>
    <div className={`wv-card ${cardClassName}`.trim()} style={cardStyle}>
      <div className="wv-cardHeader px-4 py-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <div className="d-inline-flex align-items-center gap-2 overflow-hidden">
          <i className={`bi ${icon}`}></i>
          <div className="overflow-hidden">
            <div className="wv-sectionTitle text-truncate">{title}</div>
            {subtitle ? <div className="wv-muted small text-truncate">{subtitle}</div> : null}
          </div>
        </div>
        {right}
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  </section>
);


const ScenarioCards = ({ steps = [] }) => (
  <div className="wv-scenarioGrid">
    {steps.map((step, idx) => {
      const primary = step.location || step.title || `Étape ${idx + 1}`;
      const description = step.description || step.text || step.details || '';
      const rawCount =
        step.count ??
        step.mediaCount ??
        step.media_count ??
        step.assetsCount ??
        step.assets_count ??
        step.contributionsCount ??
        step.contributions_count ??
        (Array.isArray(step.mediaIds) ? step.mediaIds.length : null) ??
        (Array.isArray(step.medias) ? step.medias.length : null) ??
        (Array.isArray(step.assets) ? step.assets.length : null);

      const count = Number.isFinite(Number(rawCount)) ? Number(rawCount) : 0;

      return (
        <div key={idx} className="wv-scenarioStepCard">
          <div className="d-flex align-items-center gap-2">
            <span className="wv-stepPill">Étape #{idx + 1}</span>
          </div>

          <div className="wv-scenarioLoc">
            <i className="bi bi-geo-alt"></i>
            <span className="text-truncate">{primary}</span>
          </div>

          {description ? (
            <div className="wv-scenarioDesc" style={{ whiteSpace: 'pre-wrap' }}>
              {description}
            </div>
          ) : (
            <div className="wv-scenarioDesc wv-muted">—</div>
          )}
        </div>
      );
    })}
  </div>
);



const LazyVideoThumb = ({ src, className = '' }) => {
  const holderRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return () => {};
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '650px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={holderRef} className={`wv-mediaThumb wv-mediaThumb--video ${className}`}>
      {active ? (
        <video src={src} muted playsInline preload="metadata" />
      ) : (
        <div className="wv-videoPlaceholder" aria-hidden="true">
          <i className="bi bi-camera-video"></i>
        </div>
      )}
    </div>
  );
};

const ProjectRecap = ({ project, finalVideoUrl }) => {
  const { t, lang, locale } = useWeavevoI18n(PR_I18N, PR_LANG_KEY);
  const auth = useAuth();
  const token = auth?.token;

  const decoded = useMemo(() => (token ? jwtDecode(token) : {}), [token]);
  const currentUser = decoded?.preferred_username || '';

  // --- Load real users + project media/messages from DB (same logic spirit as JourneyAdmin) ---
  const [usersByUsername, setUsersByUsername] = useState({});
  const [projectMedia, setProjectMedia] = useState(Array.isArray(project?.media) ? project.media : []);
  const [projectMessages, setProjectMessages] = useState(Array.isArray(project?.messages) ? project.messages : []);

  const authHeaders = useCallback(() => {
    const t = token || localStorage.getItem('kc_token') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  const fetchJsonFirst = useCallback(async (endpoints = [], options = {}) => {
    for (const url of endpoints) {
      try {
        const res = await fetch(url, options);
        if (!res.ok) continue;
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (ct.includes('application/json')) return await res.json();
        // Avoid SPA fallback HTML
        const txt = await res.text();
        if (!txt || txt.trim().startsWith('<')) continue;
        try { return JSON.parse(txt); } catch { /* ignore */ }
      } catch { /* ignore */ }
    }
    return null;
  }, []);

  // IMPORTANT: no API prefix anymore (legacy routes). Use clean root paths.
  const apiFetchJson = useCallback(async (path, options = {}) => {
    const p = String(path || '');
    const clean = /^https?:\/\//i.test(p) ? p : (p.startsWith('/') ? p : `/${p}`);
    return await fetchJsonFirst([clean], options);
  }, [fetchJsonFirst]);

  const apiFetchAny = useCallback(async (path, options = {}) => {
    const p = String(path || '');
    const clean = /^https?:\/\//i.test(p) ? p : (p.startsWith('/') ? p : `/${p}`);
    try {
      return await fetch(clean, options);
    } catch {
      return null;
    }
  }, []);

  // Backwards compatible alias (some handlers expect apiFetch)
  const apiFetch = apiFetchAny;

  const safeJson = useCallback(async (res) => {
    if (!res) return null;
    try {
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  const resolveUserKey = useCallback((identifier) => {
    const raw = String(identifier || '').trim();
    if (!raw) return '';
    if (usersByUsername?.[raw]) return raw;

    const lower = raw.toLowerCase();
    const keys = Object.keys(usersByUsername || {});

    // 1) Case-insensitive key match
    const keyMatch = keys.find((k) => String(k).toLowerCase() === lower);
    if (keyMatch) return keyMatch;

    // 2) Match by user.username / preferred_username
    for (const k of keys) {
      const u = usersByUsername?.[k] || {};
      const cand = String(u.username || u.preferred_username || u.preferredUsername || '').toLowerCase();
      if (cand && cand === lower) return k;
    }

    // 3) Match by email (common in forum messages)
    for (const k of keys) {
      const u = usersByUsername?.[k] || {};
      const emails = [];
      if (u.email) emails.push(u.email);
      if (u.mail) emails.push(u.mail);
      if (u.emailAddress) emails.push(u.emailAddress);
      if (Array.isArray(u.emails)) emails.push(...u.emails);
      if (Array.isArray(u?.attributes?.email)) emails.push(...u.attributes.email);
      if (emails.some((e) => String(e).toLowerCase() === lower)) return k;
    }

    // 4) Match by id/sub/uuid (Keycloak identifiers)
    for (const k of keys) {
      const u = usersByUsername?.[k] || {};
      const ids = [
        u.id,
        u.user_id,
        u.userId,
        u.keycloak_id,
        u.keycloakId,
        u.sub,
        u.uuid,
        u.uid
      ].filter(Boolean);
      if (Array.isArray(u?.attributes?.sub)) ids.push(...u.attributes.sub);
      if (ids.some((v) => String(v) === raw)) return k;
    }

    // Fallback: return what we got so Avatar can still show something
    return raw;
  }, [usersByUsername]);

  const displayNameFor = useCallback((identifier) => {
    const key = resolveUserKey(identifier);
    const u = usersByUsername?.[key];
    const name =
      u?.name ||
      u?.full_name ||
      u?.display_name ||
      u?.displayName ||
      u?.preferred_username ||
      u?.preferredUsername ||
      [u?.first_name, u?.last_name].filter(Boolean).join(' ') ||
      '';
    return (name || key || identifier || 'Anonyme').trim();
  }, [usersByUsername, resolveUserKey]);

  const avatarFor = useCallback((identifier) => {
    const key = resolveUserKey(identifier);
    const u = usersByUsername?.[key] || {};
    const raw =
      firstVal(
        u.avatar_url ||
        u.avatarUrl ||
        u.avatar ||
        u.photo_url ||
        u.photoUrl ||
        u.photo ||
        u.picture ||
        u.picture_url ||
        u.profile_picture ||
        u.profilePicture ||
        u.profile_photo ||
        u.profilePhoto ||
        u.profile_image ||
        u.profileImage ||
        u.identity_image ||
        u.identityImage ||
        u.identity_photo ||
        u.identityPhoto ||
        u.image_url ||
        u.imageUrl ||
        u.img ||
        u.image ||
        u?.attributes?.avatar_url ||
        u?.attributes?.picture ||
        u?.attributes?.photo ||
        u?.attributes?.identity_image
      ) || '';

    const normalized = normalizeUploadsUrl(raw);
    if (normalized) return normalized;

    const seed = u?.name || u?.full_name || u?.display_name || key || 'user';
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed)}`;
  }, [usersByUsername, resolveUserKey]);

  useEffect(() => {
    // Keep initial data from props while DB loads (fast paint)
    setProjectMedia(Array.isArray(project?.media) ? project.media : []);
    setProjectMessages(Array.isArray(project?.messages) ? project.messages : []);
  }, [project?.id]); 

  // Load users directory (avatars + real names)
  useEffect(() => {
    if (!token) return;
    (async () => {
      const arr = await fetchJsonFirst(
        ['/users'],
        { headers: { ...authHeaders() } }
      );
      if (!Array.isArray(arr)) return;

      const map = {};
      (arr || []).forEach((u) => {
        const mainKeys = [
          u?.username,
          u?.preferred_username,
          u?.preferredUsername,
          u?.user,
          u?.login,
        ].filter(Boolean);

        const idKeys = [
          u?.id,
          u?.user_id,
          u?.userId,
          u?.keycloak_id,
          u?.keycloakId,
          u?.sub,
          u?.uuid,
          u?.uid,
        ]
          .filter(Boolean)
          .map((v) => String(v));

        const emailKeys = [u?.email, u?.mail, u?.emailAddress].filter(Boolean);

        [...mainKeys, ...idKeys, ...emailKeys].forEach((k) => {
          if (!k) return;
          if (!map[k]) map[k] = u;
        });
      });

      setUsersByUsername(map);
    })();
  }, [token, authHeaders, fetchJsonFirst]);

  // Load real media + forum messages from DB (recap must not rely only on props)
  const refreshRecapData = useCallback(async () => {
    if (!project?.id) return;
    const headers = { ...authHeaders() };

    const mediaRows = await fetchJsonFirst(
      [`/projects/${project.id}/media/list`, `/projects/${project.id}/media`],
      { headers }
    );
    if (Array.isArray(mediaRows)) setProjectMedia(mediaRows);

    const messageRows = await fetchJsonFirst(
      [`/projects/${project.id}/messages`],
      { headers }
    );
    if (Array.isArray(messageRows)) setProjectMessages(messageRows);
  }, [project?.id, authHeaders, fetchJsonFirst]);

  useEffect(() => { refreshRecapData(); }, [refreshRecapData]);


  const [shareTitle, setShareTitle] = useState(project?.title || '');
  const [shareDescription, setShareDescription] = useState(project?.description || '');
  const [isPublishing, setIsPublishing] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState(project?.youtube_video_id || null);
  const [youtubeStats, setYoutubeStats] = useState(null);
  // YouTube connection (per-user OAuth)
  const [ytConnected, setYtConnected] = useState(false);
  const [ytChannelTitle, setYtChannelTitle] = useState(null);
  const [ytChannelId, setYtChannelId] = useState(null);
  const [ytBusy, setYtBusy] = useState(false);

  const [userRole, setUserRole] = useState(null);

  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null); // { url, isVideo, title, subtitle }
const [showForum, setShowForum] = useState(false);
  const [showScenario, setShowScenario] = useState(true);
  const [showComments, setShowComments] = useState(false);

  const [forumQuery, setForumQuery] = useState('');
  const forumListRef = useRef(null);
  const forumEndRef = useRef(null);

  const forumParticipants = useMemo(() => {
    const set = new Set();
    (Array.isArray(projectMessages) ? projectMessages : []).forEach((m) => {
      const u =
        m?.original_sender ||
        m?.sender ||
        m?.username ||
        m?.user ||
        m?.user_id ||
        m?.sender_id ||
        m?.created_by ||
        m?.author ||
        m?.from ||
        '';
      if (!u) return;
      const key = resolveUserKey(u);
      if (key) set.add(String(key));
    });
    return Array.from(set);
  }, [projectMessages, resolveUserKey]);

  const forumPeople = useMemo(() => forumParticipants.slice(0, 6), [forumParticipants]);
  const forumMoreCount = Math.max(0, forumParticipants.length - forumPeople.length);

  const forumMessagesSorted = useMemo(() => {
    const list = Array.isArray(projectMessages) ? projectMessages.slice() : [];
    list.sort((a, b) => new Date(a?.created_at || a?.createdAt || a?.date || 0) - new Date(b?.created_at || b?.createdAt || b?.date || 0));
    const q = String(forumQuery || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((m) => {
      const u =
        m?.original_sender ||
        m?.sender ||
        m?.username ||
        m?.user ||
        m?.user_id ||
        m?.created_by ||
        m?.author ||
        m?.from ||
        '';
      const name = displayNameFor(u);
      const text = String(m?.message || m?.content || m?.text || m?.body || '').toLowerCase();
      return String(u).toLowerCase().includes(q) || String(name).toLowerCase().includes(q) || text.includes(q);
    });
  }, [projectMessages, forumQuery, displayNameFor]);

  const dayKey = useCallback((d) => {
    const dt = new Date(d || 0);
    if (!Number.isFinite(dt.getTime())) return 'unknown';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const da = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  }, []);

  const formatDayLabel = useCallback((d) => {
    const dt = new Date(d || 0);
    if (!Number.isFinite(dt.getTime())) return '—';
    return dt.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  }, []);

  const formatTimeLabel = useCallback((d) => {
    const dt = new Date(d || 0);
    if (!Number.isFinite(dt.getTime())) return '';
    return dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }, []);

  const scrollForumToBottom = useCallback((smooth = true) => {
    if (forumEndRef.current) {
      forumEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
      return;
    }
    const el = forumListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!showForum) return;
    // When opening or when messages are loaded, keep the UX focused on the latest content.
    requestAnimationFrame(() => scrollForumToBottom(false));
  }, [showForum, forumMessagesSorted.length, scrollForumToBottom]);

  const [showGallery, setShowGallery] = useState(true);

  const [activeSection, setActiveSection] = useState('video');

  const isOwner = useMemo(() => project?.created_by === currentUser, [project?.created_by, currentUser]);
  const isCoWriter = useMemo(() => userRole === 'co-writer', [userRole]);
  const isContributor = useMemo(() => userRole === 'contributor', [userRole]);
  const isInternal = useMemo(() => isOwner || isCoWriter || isContributor, [isOwner, isCoWriter, isContributor]);

  const steps = useMemo(() => {
    const raw = project?.scenario?.steps;
    return raw ? safeJsonParse(raw, []) : [];
  }, [project?.scenario]);
  // Dummy analytics data (replace with live data if available)
  const sampleRealtimeData = useMemo(
    () => ([
      { time: '-60 min', views: 0 },
      { time: '-45 min', views: 2 },
      { time: '-30 min', views: 4 },
      { time: '-15 min', views: 5 },
      { time: 'Maintenant', views: 7 },
    ]),
    []
  );

  const dailyLikes = useMemo(
    () => ([
      { day: 'Avant', likes: 4 },
      { day: 'Aujourd’hui', likes: 32 },
    ]),
    []
  );

  const dailyComments = useMemo(
    () => ([
      { day: 'Avant', comments: 0 },
      { day: 'Aujourd’hui', comments: 6 },
    ]),
    []
  );

    // Load share data + stats
  const loadShareAndStats = useCallback(async () => {
    if (!project?.id) return;
    try {
      const data = await apiFetchJson(`/projects/${project.id}/share-data`, { headers: { ...authHeaders() } });
      if (!data) throw new Error('Pas encore partagé');

      // Refresh YT stats server-side (best-effort), then re-read
      if (data?.video_id) {
        try {
          await apiFetchJson(`/youtube-stats/${data.video_id}`, { headers: { ...authHeaders() } });
        } catch {}
      }

      const updated = await apiFetchJson(`/projects/${project.id}/share-data`, { headers: { ...authHeaders() } });
      if (!updated) return;

      setYoutubeVideoId(updated.video_id);
      setShareTitle(updated.title);
      setShareDescription(updated.description);
      setYoutubeStats({
        viewCount: Number(updated.view_count ?? 0),
        likeCount: Number(updated.like_count ?? 0),
        commentCount: Number(updated.comment_count ?? 0)
      });
    } catch {
      // No share yet or transient error — keep silent
    }
  }, [project?.id, authHeaders, apiFetchJson]);


  const refreshYoutubeStatus = useCallback(async () => {
    try {
      const j = await apiFetchJson('/auth/youtube/status', { headers: { ...authHeaders() } });
      if (!j) {
        setYtConnected(false);
        setYtChannelTitle(null);
        setYtChannelId(null);
        return;
      }
      setYtConnected(!!j.connected);
      setYtChannelTitle(j.channelTitle || null);
      setYtChannelId(j.channelId || null);
    } catch {
      // keep silent
    }
  }, [authHeaders, apiFetchJson]);

useEffect(() => {
    if (!token) return;
    refreshYoutubeStatus();
  }, [token, refreshYoutubeStatus]);

  const connectYoutube = useCallback(async () => {
    setYtBusy(true);
    try {
      const returnTo = window.location.pathname + window.location.search;
      const j = await apiFetchJson(`/auth/youtube/connect-url?returnTo=${encodeURIComponent(returnTo)}`, {
        headers: { ...authHeaders() }
      });
      if (!j?.url) throw new Error(j?.error || 'Cannot start YouTube connect');
      window.location.href = j.url; // redirect to Google consent
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || 'YouTube connect failed');
      setYtBusy(false);
    }
  }, [authHeaders, apiFetchJson]);

const disconnectYoutube = useCallback(async () => {
    setYtBusy(true);
    try {
      const res = await apiFetch('/auth/youtube/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (!res) throw new Error('Disconnect failed');
      await refreshYoutubeStatus();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || 'YouTube disconnect failed');
    } finally {
      setYtBusy(false);
    }
  }, [authHeaders, refreshYoutubeStatus, apiFetch]);

// Initial role
  useEffect(() => {
    if (!token || !project?.id) return;
    (async () => {
      try {
        const data = await apiFetchJson(`/projects/${project.id}/roles`, { headers: { ...authHeaders() } });
        if (!Array.isArray(data)) return;
        const roleEntry = data.find((r) => r.username === currentUser);
        if (roleEntry) setUserRole(roleEntry.role);
      } catch {}
    })();
  }, [token, project?.id, currentUser, authHeaders, apiFetchJson]);

useEffect(() => {
    if (project?.id) loadShareAndStats();
  }, [project?.id, loadShareAndStats]);

  // Group media by contributor (DB-loaded)
  const groupedByContributor = useMemo(() => {
    const g = {};
    (projectMedia || []).forEach((item) => {
      const contributor =
        item.contributor ||
        item.username ||
        item.user_id ||
        item.created_by ||
        'Anonyme';
      if (!g[contributor]) g[contributor] = [];
      g[contributor].push(item);
    });
    return g;
  }, [projectMedia]);

const contributorNames = useMemo(() => Object.keys(groupedByContributor), [groupedByContributor]);
  const contributorCount = useMemo(() => contributorNames.length, [contributorNames]);

  const mediaCount = useMemo(() => (projectMedia || []).length, [projectMedia]);
  const messageCount = useMemo(() => (projectMessages || []).length, [projectMessages]);

const imageCount = useMemo(
    () => (projectMedia || []).filter((m) => !isVideoPath(m.file_path || '')).length,
    [projectMedia]
  );
  const videoCount = useMemo(
    () => (projectMedia || []).filter((m) => isVideoPath(m.file_path || '')).length,
    [projectMedia]
  );

const [galleryContributor, setGalleryContributor] = useState('Tous');
  const [galleryType, setGalleryType] = useState('all'); // all | images | videos

  useEffect(() => {
    // Reset filter when project changes
    setGalleryContributor('Tous');
    setGalleryType('all');
  }, [project?.id]);

  const flattenedMedia = useMemo(() => {
    const list = [];
    Object.entries(groupedByContributor).forEach(([contributor, items]) => {
      items.forEach((m) => list.push({ ...m, __contributor: contributor }));
    });
    return list;
  }, [groupedByContributor]);

  const filteredMedia = useMemo(() => {
    let list = flattenedMedia;
    if (galleryContributor !== 'Tous') list = list.filter((m) => m.__contributor === galleryContributor);
    if (galleryType === 'images') list = list.filter((m) => !isVideoPath(m.file_path || ''));
    if (galleryType === 'videos') list = list.filter((m) => isVideoPath(m.file_path || ''));
    return list;
  }, [flattenedMedia, galleryContributor, galleryType]);

  const estimatedRevenue = useMemo(() => {
    const views = Number(youtubeStats?.viewCount || 0);
    const cpm = 3.0; // demo CPM
    return (views / 1000) * cpm;
  }, [youtubeStats]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(finalVideoSafe || finalVideoUrl || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scrollspy (active chip)
  useEffect(() => {
    const ids = ['video', 'distribution', 'scenario', 'forum', 'gallery', 'share'].filter((id) => {
      if (id === 'distribution') return isOwner || isCoWriter || !!youtubeVideoId;
      if (id === 'scenario') return isInternal && !!project?.scenario;
      if (id === 'forum') return isInternal && Array.isArray(projectMessages);
      if (id === 'gallery') return isInternal;
      return true;
    });

    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return () => {};

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top > b.boundingClientRect.top ? 1 : -1));
        if (visible[0]?.target?.id) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0.05, 0.2, 0.35] }
    );

    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [isInternal, isOwner, isCoWriter, youtubeVideoId, project?.scenario, projectMessages]);

  // Close preview on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const recapStyles = `
/* Overall */
.project-recap{
  --wv-border: rgba(15,23,42,.10);
  --wv-soft: rgba(15,23,42,.06);
  --wv-ink: #0f172a;
  --wv-muted: rgba(15,23,42,.70);
  --wv-surface: rgba(255,255,255,.92);
}
.project-recap.page-wrap{ width: 100%; max-width: 100% !important; }
.project-recap .wv-muted{ color: var(--wv-muted); }
.project-recap .wv-section{ scroll-margin-top: 130px; margin-bottom: 18px; }

/* Header */
.project-recap .wv-recapHeader h2{
  font-weight: 600; /* keep readable, not too bold */
}
.project-recap .wv-kpiGrid{
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 992px){
  .project-recap .wv-kpiGrid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.project-recap .wv-kpi{
  border: 1px solid var(--wv-border);
  background: var(--wv-surface);
  border-radius: 18px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.project-recap .wv-kpiIcon{
  width: 36px;
  height: 36px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: var(--wv-soft);
  border: 1px solid var(--wv-border);
  color: var(--wv-ink);
}
.project-recap .wv-kpiVal{
  font-size: 18px;
  font-weight: 600;
  line-height: 1.1;
}
.project-recap .wv-kpiLbl{
  font-size: 12px;
  color: rgba(15,23,42,.62);
}

/* Sticky dock */
.project-recap .wv-topbar{
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  padding: 10px 12px;
  background: rgba(250,250,250,.92);
  backdrop-filter: blur(10px);

  border: 1px solid var(--wv-border);
  border-radius: 18px;

  position: sticky;
  top: 70px;
  z-index: 500;
}
.project-recap .wv-topbarGroup{
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.project-recap .wv-cta{
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}
@media (max-width: 820px){
  .project-recap .wv-cta{
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

/* Chips */
.project-recap .wv-chip{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 999px;

  cursor: pointer;
  user-select: none;

  font-weight: 500;
  font-size: 13px;
  line-height: 1;

  border: 1px solid var(--wv-border);
  background: #fff;
  color: var(--wv-ink);

  transition: transform .12s ease, box-shadow .15s ease, background .15s ease;
  white-space: nowrap;
  text-decoration: none;
}
.project-recap .wv-chip:hover{
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(15,23,42,.08);
}
.project-recap .wv-chip:active{
  transform: translateY(0);
  box-shadow: 0 6px 14px rgba(15,23,42,.10);
}
.project-recap .wv-chip--primary{
  background: #111827;
  color: #fff;
  border-color: rgba(17,24,39,.45);
}
.project-recap .wv-chip--primary:hover{ box-shadow: 0 14px 26px rgba(2,6,23,.18); }

.project-recap .wv-chip--static{ cursor: default; }
.project-recap .wv-chip--static:hover{ transform: none; box-shadow: none; }

.project-recap .wv-chipValue{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(15,23,42,.06);
  border: 1px solid rgba(15,23,42,.08);
  font-size: 12px;
  font-weight: 700;
  color: rgba(15,23,42,.76);
}
.project-recap .wv-chip--active{
  background: rgba(17,24,39,.08);
  border-color: rgba(17,24,39,.22);
}

/* Scenario (cards like the attached design) */
.project-recap .wv-scenarioGrid{
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.project-recap .wv-scenarioStepCard{
  border: 1px solid var(--wv-border);
  background: rgba(248,250,252,.96);
  border-radius: 16px;
  padding: 12px 12px;
  min-height: 120px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.project-recap .wv-stepPill{
  display: inline-flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 999px;
  font-weight: 650;
  font-size: 11px;

  color: rgba(29,78,216,1);
  background: rgba(59,130,246,.12);
  border: 1px solid rgba(59,130,246,.28);
}
.project-recap .wv-stepCount{
  margin-left: auto;
  font-weight: 700;
  font-size: 16px;
  color: rgba(15,23,42,.90);
}
.project-recap .wv-scenarioLoc{
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  font-weight: 650;
  font-size: 14px;
  line-height: 1.2;
  color: rgba(15,23,42,.92);
}
.project-recap .wv-scenarioLoc i{
  font-size: 14px;
  opacity: .85;
}
.project-recap .wv-scenarioDesc{
  margin-top: 8px;
  color: rgba(15,23,42,.68);
  font-size: 12.5px;
  line-height: 1.45;
  flex: 1;
}

/* Section header */
.project-recap .wv-sectionTitle{
  font-weight: 600;
  line-height: 1.1;
}
.project-recap .wv-cardHeader i{ opacity: .9; }

/* Video shell */

/* Keep "Vidéo finale" + "Diffusion" always inline (never stacked).
   On smaller screens, we keep them side-by-side with horizontal scrolling. */
.project-recap .wv-recapFinalRow{
  display: flex;
  align-items: stretch;
  gap: 16px;
  flex-wrap: nowrap;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 2px;
}
.project-recap .wv-recapFinalCol{ min-width: 0; }
.project-recap .wv-recapFinalCol--video{
  flex: 1 1 720px;
  min-width: 560px;
}
.project-recap .wv-recapFinalCol--dist{
  flex: 0 0 420px;
  min-width: 340px;
}
.project-recap .wv-recapFinalRow::-webkit-scrollbar{ height: 10px; }
.project-recap .wv-recapFinalRow::-webkit-scrollbar-thumb{ background: rgba(15,23,42,.18); border-radius: 999px; }
.project-recap .wv-recapFinalRow::-webkit-scrollbar-track{ background: transparent; }

.project-recap .wv-videoShell{
  border: 1px solid var(--wv-border);
  border-radius: 18px;
  overflow: hidden;
  background: #000;
}
.project-recap .wv-videoShell video{ display: block; width: 100%; height: 100%; }

.project-recap .wv-videoMetaBar{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255,255,255,.96);
  border-top: 1px solid rgba(15,23,42,.10);
}
.project-recap .wv-avatarStack{
  display: flex;
  align-items: center;
}
.project-recap .wv-avatarStackItem{
  border-radius: 999px;
  margin-left: -12px;
}
.project-recap .wv-avatarStackItem:first-child{ margin-left: 0; }
.project-recap .wv-videoContribAvatar{ box-shadow: none !important; }
.project-recap .wv-avatarMore{
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid #fff;
  margin-left: -10px;
}
.project-recap .wv-videoActionsClean{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.project-recap .wv-actionPill{
  height: 42px;
  padding: 0 14px;
  border-radius: 999px;
  border: 0;
  background: rgba(255,255,255,.92);
  color: var(--wv-ink);
  box-shadow: 0 10px 22px rgba(2,6,23,.10);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: transform .12s ease, box-shadow .15s ease;
}
.project-recap .wv-actionPill:hover{
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(2,6,23,.12);
}
.project-recap .wv-actionPill:active{ transform: translateY(0); }
.project-recap .wv-actionPill:disabled,
.project-recap .wv-actionPill.disabled{
  opacity: .55;
  cursor: not-allowed;
  transform: none;
}
.project-recap .wv-actionCount{
  font-size: 13px;
  font-weight: 700;
}
.project-recap .wv-actionPill--like.active i,
.project-recap .wv-actionPill--like.active .wv-actionCount{
  color: #e11d48;
}
.project-recap .wv-menuWrap{ position: relative; }
.project-recap .wv-menu{
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 200px;
  background: #fff;
  border: 1px solid var(--wv-border);
  border-radius: 16px;
  padding: 6px;
  box-shadow: 0 20px 50px rgba(2,6,23,.18);
  z-index: 80;
}
.project-recap .wv-menu button,
.project-recap .wv-menu a{
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--wv-ink);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 12px;
  cursor: pointer;
}
.project-recap .wv-menu button:hover,
.project-recap .wv-menu a:hover{
  background: rgba(15,23,42,.06);
}
.project-recap .wv-videoTitleBlock{
  padding: 12px;
  background: rgba(255,255,255,.96);
}
.project-recap .wv-videoTitle{
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
}
.project-recap .wv-videoBadge{
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(17,24,39,.08);
  border: 1px solid rgba(17,24,39,.14);
  font-size: 13px;
  font-weight: 600;
}
.project-recap .wv-videoSubtitle{
  margin-top: 6px;
  color: var(--wv-muted);
}

.project-recap .wv-videoActions{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px;
  background: rgba(255,255,255,.92);
  border-top: 1px solid rgba(255,255,255,.18);
}
.project-recap .wv-videoActions .btn{ border-radius: 999px; }

/* Gallery */
.project-recap .wv-filtersRow{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
}
.project-recap .wv-filterPills{
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.project-recap .wv-contribPill{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid var(--wv-border);
  background: #fff;
  cursor: pointer;
  text-decoration: none;
  color: var(--wv-ink);
}
.project-recap .wv-contribPill:hover{
  box-shadow: 0 10px 20px rgba(15,23,42,.08);
  transform: translateY(-1px);
}
.project-recap .wv-contribPill--active{
  background: rgba(17,24,39,.08);
  border-color: rgba(17,24,39,.22);
}

/* Media thumb */
.project-recap .wv-mediaBtn{
  padding: 0;
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
}
.project-recap .wv-mediaCard{
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--wv-border);
  background: #fff;
}
.project-recap .wv-mediaThumb{
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #0b1020;
}
.project-recap .wv-mediaThumb img,
.project-recap .wv-mediaThumb video{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.project-recap .wv-videoPlaceholder{
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: rgba(255,255,255,.8);
  font-size: 22px;
}
.project-recap .wv-mediaOverlay{
  position: absolute;
  inset: auto 10px 10px 10px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  pointer-events: none;
}
.project-recap .wv-mediaOverlay .badge{ border-radius: 999px; }

/* Modal */
.project-recap .wv-modalBackdrop{
  position: fixed;
  inset: 0;
  background: rgba(2,6,23,.62);
  display: grid;
  place-items: center;
  z-index: 2000;
  padding: 18px;
}
.project-recap .wv-modalCard{
  width: min(980px, 96vw);
  max-height: 92vh;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.96);
  box-shadow: 0 30px 80px rgba(2,6,23,.45);
  display: flex;
  flex-direction: column;
}
.project-recap .wv-modalClose{
  position: absolute;
  top: 12px;
  right: 12px;
}
.project-recap .wv-modalMedia{
  background: #000;
  display: grid;
  place-items: center;
  flex: 1 1 auto;
}
.project-recap .wv-modalMedia img,
.project-recap .wv-modalMedia video{
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.project-recap .wv-modalFooter{
  padding: 12px 14px;
  border-top: 1px solid var(--wv-border);
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Final comments */
.project-recap .wv-toast{
  display:flex;
  align-items:center;
  gap:10px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,.10);
  background: rgba(248,250,252,.92);
  margin-bottom: 12px;
  box-shadow: 0 8px 22px rgba(16,24,40,.06);
}
.project-recap .wv-toast i{ font-size: 16px; }
.project-recap .wv-toast--success{ border-color: rgba(16,185,129,.28); background: rgba(16,185,129,.08); }
.project-recap .wv-toast--error{ border-color: rgba(239,68,68,.28); background: rgba(239,68,68,.08); }
.project-recap .wv-toast--info{ border-color: rgba(59,130,246,.28); background: rgba(59,130,246,.08); }

.project-recap .wv-commentsWrap{ width: 100%; }
.project-recap .wv-commentComposer{
  display:flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}
.project-recap .wv-commentInput{
  border-radius: 16px;
  resize: vertical;
}
.project-recap .wv-commentList{
  display:flex;
  flex-direction: column;
  gap: 10px;
}
.project-recap .wv-commentBubble{
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  border-radius: 16px;
  padding: 10px 12px;
  box-shadow: 0 8px 18px rgba(16,24,40,.05);
}
.project-recap .wv-commentAuthor{
  font-weight: 650;
  font-size: 13px;
  color: rgba(15,23,42,.9);
}
.project-recap .wv-commentDate{
  font-size: 11px;
  color: rgba(15,23,42,.45);
  white-space: nowrap;
}
.project-recap .wv-commentText{
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(15,23,42,.76);
  white-space: pre-wrap;
}

/* Realtime card (more compact, no dead space) */
.project-recap .wv-rtCard{
  border: 1px solid var(--wv-border);
  background: rgba(248,250,252,.90);
  border-radius: 18px;
  padding: 14px;
}
.project-recap .wv-rtHeader{
  display:flex;
  align-items:center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.project-recap .wv-rtTitle{
  display:flex;
  align-items:center;
  gap: 10px;
  color: rgba(15,23,42,.75);
  font-weight: 650;
}
.project-recap .wv-rtGrid{
  display:grid;
  grid-template-columns: 1.6fr .9fr;
  gap: 10px;
  align-items: stretch;
}
@media (max-width: 900px){
  .project-recap .wv-rtGrid{ grid-template-columns: 1fr; }
}
.project-recap .wv-rtChart{
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.06);
  padding: 8px;
  height: 160px;
}
.project-recap .wv-rtSide{
  display:grid;
  gap: 10px;
  align-content: start;
}
.project-recap .wv-rtMini{
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.06);
  padding: 10px;
}
.project-recap .wv-rtMiniLabel{
  display:flex;
  align-items:center;
  gap: 8px;
  font-size: 12px;
  color: rgba(15,23,42,.62);
  margin-bottom: 8px;
}
.project-recap .wv-rtMiniChart{ height: 74px; }
`;

  const navItems = useMemo(() => {
    const items = [
      { id: 'video', label: 'Vidéo', icon: 'bi-play-circle' },
      { id: 'distribution', label: 'Diffusion', icon: 'bi-youtube' },
      { id: 'scenario', label: 'Scénario', icon: 'bi-journal-richtext' },
      { id: 'forum', label: 'Forum', icon: 'bi-chat-left-text' },
      { id: 'gallery', label: 'Galerie', icon: 'bi-collection' },
      { id: 'share', label: 'Partager', icon: 'bi-share' },
    ];

    return items.filter((it) => {
      if (it.id === 'distribution') return (isOwner || isCoWriter) || !!youtubeVideoId;
      if (it.id === 'scenario') return isInternal && !!project?.scenario;
      if (it.id === 'forum') return isInternal && Array.isArray(projectMessages);
      if (it.id === 'gallery') return isInternal;
      return true;
    });
  }, [isInternal, isOwner, isCoWriter, youtubeVideoId, project?.scenario, projectMessages]);

  const contributorAvatars = useMemo(() => contributorNames.slice(0, 6), [contributorNames]);

  const finalVideoSafe = finalVideoUrl || '';
  // Contributors to the FINAL video (prefer selected clips when DB provides a flag; fallback to all)
  const mediaForFinal = useMemo(() => {
    const list = Array.isArray(projectMedia) ? projectMedia : [];
    const hasSelectionFlag = list.some((m) =>
      m?.selected === 1 || m?.selected === true ||
      m?.is_selected === 1 || m?.used === 1 ||
      m?.in_final === 1 || m?.used_in_final === 1
    );
    if (!hasSelectionFlag) return list;
    return list.filter((m) =>
      m?.selected === 1 || m?.selected === true ||
      m?.is_selected === 1 || m?.used === 1 ||
      m?.in_final === 1 || m?.used_in_final === 1
    );
  }, [projectMedia]);

  const finalContributors = useMemo(() => {
    const counts = {};
    (mediaForFinal || []).forEach((m) => {
      const u = m?.contributor || m?.username || m?.user_id || m?.created_by;
      if (!u) return;
      counts[u] = (counts[u] || 0) + 1;
    });
    return Object.keys(counts).sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
  }, [mediaForFinal]);

  const videoContributors = useMemo(() => finalContributors.slice(0, 6), [finalContributors]);
  const extraContribCount = Math.max(0, finalContributors.length - videoContributors.length);

  // Final video reactions + comments (same logic as MediaDetail.js)
const getAuthToken = useCallback(
  () => token || localStorage.getItem('kc_token') || localStorage.getItem('token'),
  [token]
);

const [likesCount, setLikesCount] = useState(null);
const [hasLiked, setHasLiked] = useState(false);
const [reactionsLoading, setReactionsLoading] = useState(false);

const [finalComments, setFinalComments] = useState([]);
const [newComment, setNewComment] = useState('');
const [commentLoading, setCommentLoading] = useState(false);

const [toast, setToast] = useState(null); // { type: 'success'|'error'|'info', message }
const toastTimerRef = useRef(null);

const showToast = useCallback((type, message) => {
  try { clearTimeout(toastTimerRef.current); } catch {}
  setToast({ type, message: String(message || '') });
  toastTimerRef.current = setTimeout(() => setToast(null), 3200);
}, []);

useEffect(() => () => {
  try { clearTimeout(toastTimerRef.current); } catch {}
}, []);

const loadFinalReactions = useCallback(async () => {
  if (!project?.id) return;
  try {
    const t = getAuthToken();
    const headers = { ...(t ? { Authorization: `Bearer ${t}` } : {}) };
    const resp = await apiFetchAny(`/projects/${project.id}/final/reactions`, { headers });
    const j = await safeJson(resp);
    if (!j) return;
    setLikesCount(j.likes_count ?? 0);
    setHasLiked(!!j.user_has_liked);
  } catch (e) {
    // silent: recap screen should stay usable even if endpoint unavailable
    console.warn('Erreur final reactions:', e);
  }
}, [project?.id, getAuthToken, apiFetchAny, safeJson]);

const loadFinalComments = useCallback(async () => {
  if (!project?.id) return;
  try {
    const t = getAuthToken();
    const headers = { ...(t ? { Authorization: `Bearer ${t}` } : {}) };
    const resp = await apiFetchAny(`/projects/${project.id}/final/comments`, { headers });
    const j = await safeJson(resp);
    if (!j) return;
    setFinalComments(Array.isArray(j.comments) ? j.comments : (j.comments || []));
  } catch (e) {
    console.warn('Erreur commentaires:', e);
  }
}, [project?.id, getAuthToken, apiFetchAny, safeJson]);

useEffect(() => {
  if (!project?.id) return;
  loadFinalReactions();
  loadFinalComments();
}, [project?.id, token, loadFinalReactions, loadFinalComments]);

const handleToggleLike = useCallback(async () => {
  if (!project?.id) return;
  const tkn = getAuthToken();
  if (!tkn) return showToast('info', t('toast_login_like_video'));

  try {
    setReactionsLoading(true);
    const resp = await apiFetchAny(`/projects/${project.id}/final/like`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tkn}` }
    });
    if (!resp) throw new Error(t('pr_err_invalid_response'));

    const data = await safeJson(resp);
    if (!resp.ok || data?.error) throw new Error(data?.error || t('pr_err_like'));

    setLikesCount(data.likes_count ?? 0);
    setHasLiked(!!data.user_has_liked);
  } catch (e) {
    showToast('error', e?.message || t('pr_err_like'));
  } finally {
    setReactionsLoading(false);
  }
}, [project?.id, getAuthToken, showToast, t, apiFetchAny, safeJson]);

const submitFinalComment = useCallback(async () => {
  if (!project?.id) return;
  const text = String(newComment || '').trim();
  if (!text) return;

  const tkn = getAuthToken();
  if (!tkn) return showToast('info', t('toast_login_comment'));

  try {
    setCommentLoading(true);
    const resp = await apiFetchAny(`/projects/${project.id}/final/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tkn}`
      },
      body: JSON.stringify({ text })
    });

    if (!resp) throw new Error(t('pr_err_invalid_response'));
    const j = await safeJson(resp);
    if (!resp.ok) throw new Error(j?.error || 'Erreur');

    setNewComment('');
    await loadFinalComments();
  } catch (e) {
    showToast('error', e?.message || t('pr_err_comment'));
  } finally {
    setCommentLoading(false);
  }
}, [project?.id, newComment, getAuthToken, showToast, t, loadFinalComments, apiFetchAny, safeJson]);

const likeCountDisplay = useMemo(() => {
  if (typeof likesCount === 'number') return likesCount;
  return Number(youtubeStats?.likeCount ?? 0);
}, [likesCount, youtubeStats?.likeCount]);

const commentCountDisplay = useMemo(() => {
  if (isInternal) return Array.isArray(finalComments) ? finalComments.length : 0;
  return Number(youtubeStats?.commentCount ?? 0);
}, [isInternal, finalComments, youtubeStats?.commentCount]);

const shareFinalVideo = useCallback(async () => {
    if (!finalVideoSafe) return;

    const shareData = {
      title: project?.title || 'Weavevo',
      text: 'Découvrez cette vidéo collaborative.',
      url: finalVideoSafe
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // user cancelled or unsupported payload
      }
    }

    await copyLink();
  }, [finalVideoSafe, project?.title, copyLink]);

  
const onCommentsClick = useCallback(() => {
  // Internal: open Weavevo final-video comments (DB), not the Forum
  if (isInternal) {
    setShowComments(true);
    scrollToSection('comments');
    return;
  }
  // External: fallback to YouTube comments (if published)
  if (youtubeVideoId) window.open(`https://www.youtube.com/watch?v=${youtubeVideoId}`, '_blank', 'noopener,noreferrer');
}, [isInternal, youtubeVideoId, scrollToSection]);

  const [showVideoMenu, setShowVideoMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showVideoMenu) return () => {};
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowVideoMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showVideoMenu]);

  return (
    <div className="project-recap page-wrap">
      <style>{recapStyles}</style>

      {/* Header */}
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3 wv-recapHeader">
        <div className="d-flex align-items-start gap-3">
          <div className="wv-avatarIcon" style={{ width: 46, height: 46 }}>
            <i className="bi bi-camera-reels fs-5"></i>
          </div>

          <div className="flex-grow-1 overflow-hidden">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h2 className="mb-0 text-truncate" style={{ maxWidth: 'min(72vw, 920px)' }}>
                {project?.title || 'Projet'}
              </h2>
              <span className="badge text-bg-success rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2">
                <i className="bi bi-check2-circle"></i>
                <span>{t("pr_closed")}</span>
              </span>
            </div>
            {project?.description ? (
              <div className="wv-muted mt-1">{project.description}</div>
            ) : (
              <div className="wv-muted mt-1">{t("pr_subtitle")}</div>
            )}

            {isInternal && contributorNames.length > 0 && (
              <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                <div className="d-inline-flex align-items-center gap-1">
                  {contributorAvatars.map((n) => (
                    <ImgAvatar key={n} src={avatarFor(n)} label={displayNameFor(n)} size={34} />
                  ))}
                  {contributorNames.length > contributorAvatars.length && (
                    <div className="small text-muted ms-1">
                      +{contributorNames.length - contributorAvatars.length}
                    </div>
                  )}
                </div>

                <span className="small text-muted d-inline-flex align-items-center gap-2">
                  <i className="bi bi-people"></i>
                  {contributorCount} contributeur{contributorCount > 1 ? 's' : ''} • {mediaCount} média{mediaCount > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>

       
      </div>

      {/* KPI grid */}
      <div className="wv-kpiGrid mb-3">
        <div className="wv-kpi">
          <div className="wv-kpiIcon"><i className="bi bi-collection-play"></i></div>
          <div className="overflow-hidden">
            <div className="wv-kpiVal">{formatCompact(mediaCount)}</div>
            <div className="wv-kpiLbl">Médias • {imageCount} img / {videoCount} vid</div>
          </div>
        </div>

        <div className="wv-kpi">
          <div className="wv-kpiIcon"><i className="bi bi-people"></i></div>
          <div className="overflow-hidden">
            <div className="wv-kpiVal">{formatCompact(contributorCount)}</div>
            <div className="wv-kpiLbl">Contributeurs</div>
          </div>
        </div>

        <div className="wv-kpi">
          <div className="wv-kpiIcon"><i className="bi bi-chat-dots"></i></div>
          <div className="overflow-hidden">
            <div className="wv-kpiVal">{formatCompact(messageCount)}</div>
            <div className="wv-kpiLbl">Messages forum</div>
          </div>
        </div>

        <div className="wv-kpi">
          <div className="wv-kpiIcon"><i className="bi bi-signpost-split"></i></div>
          <div className="overflow-hidden">
            <div className="wv-kpiVal">{formatCompact(steps?.length || 0)}</div>
            <div className="wv-kpiLbl">Étapes scénario</div>
          </div>
        </div>
      </div>

      {/* Sticky dock: navigation + actions */}
      <div className="wv-topbar mb-4" role="navigation" aria-label="Navigation projet">
        <div className="wv-topbarGroup">
          {navItems.map((it) => (
            <button
              key={it.id}
              type="button"
              className={`wv-chip ${activeSection === it.id ? 'wv-chip--active' : ''}`}
              onClick={() => scrollToSection(it.id)}
            >
              <i className={`bi ${it.icon}`}></i>
              <span>{it.label}</span>
            </button>
          ))}
        </div>

        <div className="wv-cta">
          <a href="/public" className="wv-chip" title={t("pr_back_dashboard")}>
            <i className="bi bi-arrow-left"></i>
            <span className="d-none d-sm-inline">{t("pr_dashboard")}</span>
          </a>

          <a
            href={finalVideoSafe}
            target="_blank"
            rel="noopener noreferrer"
            className="wv-chip wv-chip--primary"
            title={t("pr_open_final_video")}
          >
            <i className="bi bi-play-circle"></i>
            <span>{t("pr_watch")}</span>
          </a>
        </div>
      </div>

      <div className="wv-recapFinalRow">
        <div className="wv-recapFinalCol wv-recapFinalCol--video">
          {/* Video */}
          <SectionShell
            id="video"
            icon="bi-film"
            title="Vidéo finale"
            subtitle="Visionnage, export et lien direct"
            
          >
            <div className="wv-videoShell">
              <div className="ratio ratio-16x9">
                {finalVideoSafe ? (
                  <video src={finalVideoSafe} controls className="w-100 h-100" />
                ) : (
                  <div className="d-flex align-items-center justify-content-center w-100 h-100 text-white">
                    <div className="text-center p-4">
                      <i className="bi bi-film fs-1"></i>
                      <div className="mt-2">{t("pr_no_final")}</div>
                      <div className="small opacity-75">{t("pr_come_back")}</div>
                    </div>
                  </div>
                )}
              </div>

              
              <div className="wv-videoMetaBar">
                <div className="wv-videoContribs" aria-label="Contributeurs">
                  <div className="wv-avatarStack">
                    {videoContributors.map((name, idx) => (
                      <div
                        key={`${name}-${idx}`}
                        className="wv-avatarStackItem"
                        style={{ zIndex: videoContributors.length - idx }}
                        title={`${displayNameFor(name)} (@${name})`}
                      >
                        <ImgAvatar src={avatarFor(name)} label={displayNameFor(name)} size={36} className="wv-videoContribAvatar" />
                      </div>
                    ))}

                    {extraContribCount > 0 && (
                      <span
                        className="wv-avatarMore"
                        title={`${finalContributors.length} contributeurs`}
                      >
                        +{extraContribCount}
                      </span>
                    )}
                  </div>
                </div>

                <div className="wv-videoActionsClean" role="group" aria-label="Actions vidéo">
                  <button
  type="button"
  className={`wv-actionPill wv-actionPill--like ${hasLiked ? 'active' : ''}`}
  onClick={handleToggleLike}
  title={hasLiked ? 'Retirer le like' : 'Aimer'}
  aria-label={hasLiked ? 'Retirer le like' : 'Aimer'}
  aria-pressed={hasLiked ? 'true' : 'false'}
  disabled={!finalVideoSafe || reactionsLoading}
>
  <i className={`bi ${hasLiked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
  <span className="wv-actionCount">{formatCompact(likeCountDisplay)}</span>
</button>

                  <button
                    type="button"
                    className="wv-actionPill"
                    onClick={onCommentsClick}
                    title="Commentaires"
                    aria-label="Commentaires"
                    disabled={!isInternal && !youtubeVideoId}
                  >
                    <i className="bi bi-chat"></i>
                    <span className="wv-actionCount">{formatCompact(commentCountDisplay)}</span>
                  </button>

                  <button
                    type="button"
                    className="wv-actionPill"
                    onClick={shareFinalVideo}
                    title={t("common_share")}
                    aria-label={t("common_share")}
                    disabled={!finalVideoSafe}
                  >
                    <i className="bi bi-share"></i>
                  </button>

                  <div className="wv-menuWrap" ref={menuRef}>
                    <button
                      type="button"
                      className="wv-actionPill wv-actionPill--menu"
                      onClick={() => setShowVideoMenu((v) => !v)}
                      title="Plus"
                      aria-label="Plus"
                      disabled={!finalVideoSafe}
                    >
                      <i className="bi bi-three-dots"></i>
                    </button>

                    {showVideoMenu && (
                      <div className="wv-menu" role="menu" aria-label="Menu vidéo">
                        <button
                          type="button"
                          onClick={() => {
                            setShowVideoMenu(false);
                            copyLink();
                          }}
                        >
                          <i className="bi bi-link-45deg"></i>
                          Copier le lien
                        </button>

                        <a
                          href={finalVideoSafe || '#'}
                          download
                          onClick={(e) => {
                            if (!finalVideoSafe) e.preventDefault();
                            setShowVideoMenu(false);
                          }}
                        >
                          <i className="bi bi-download"></i>
                          Télécharger
                        </a>

                        <a
                          href={finalVideoSafe || '#'}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            if (!finalVideoSafe) e.preventDefault();
                            setShowVideoMenu(false);
                          }}
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                          Ouvrir dans un onglet
                        </a>
                      </div>
                    )}
                  </div>

                  {copied && (
                    <span className="wv-copiedPill" role="status" title="Lien copié">
                      <i className="bi bi-check2"></i>
                      <span className="d-none d-md-inline">{t("pr_link_copied")}</span>
                    </span>
                  )}
                </div>
              </div>

              <div className="wv-videoTitleBlock">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="wv-videoTitle">{project?.title || 'Projet'}</div>
                  <span className="wv-videoBadge">{t("pr_final_of_project")}</span>
                </div>
                {project?.description ? (
                  <div className="wv-videoSubtitle">{project.description}</div>
                ) : (
                  <div className="wv-videoSubtitle">
                    {t("pr_final_desc")}
                  </div>
                )}
              </div>

            </div>
          </SectionShell>
        </div>

        {(isOwner || isCoWriter || youtubeVideoId) && (
          <div className="wv-recapFinalCol wv-recapFinalCol--dist">
          {/* Distribution / YouTube */}
          {(isOwner || isCoWriter || youtubeVideoId) && (
            <SectionShell
              id="distribution"
              icon="bi-youtube"
              title={t("pr_youtube_section_title")}
              subtitle={youtubeVideoId ? t("pr_youtube_sub_published") : t("pr_youtube_sub_publish")}
              right={
                youtubeVideoId ? (
                  <a
                    href={`https://www.youtube.com/watch?v=${youtubeVideoId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    <span className="d-none d-sm-inline">{t("pr_open")}</span>
                  </a>
                ) : null
              }
            >
              {(isOwner || isCoWriter) && (
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="small text-muted d-inline-flex align-items-center gap-2">
                    {ytConnected ? (
                      <>
                        <i className="bi bi-check-circle-fill text-success"></i>
                        <span>
                          {t("pr_youtube_connected")}
                          {ytChannelTitle ? `: ${ytChannelTitle}` : ""}
                        </span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-exclamation-circle text-warning"></i>
                        <span>{t("pr_youtube_not_connected")}</span>
                      </>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    {!ytConnected ? (
                      <button
                        type="button"
                        className="btn btn-outline-dark btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                        onClick={connectYoutube}
                        disabled={ytBusy || isPublishing}
                      >
                        <i className="bi bi-link-45deg"></i>
                        <span>{ytBusy ? t("pr_youtube_connecting") : t("pr_youtube_connect")}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                        onClick={disconnectYoutube}
                        disabled={ytBusy || isPublishing}
                      >
                        <i className="bi bi-plug"></i>
                        <span>{ytBusy ? t("pr_youtube_disconnecting") : t("pr_youtube_disconnect")}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {(isOwner || isCoWriter) && !youtubeVideoId && (
                <>
                  <div className="mb-3">
                    <label className="form-label">{t("pr_title")}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={shareTitle}
                      onChange={(e) => setShareTitle(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t("pr_description")}</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={shareDescription}
                      onChange={(e) => setShareDescription(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn btn-danger w-100 rounded-pill d-inline-flex align-items-center justify-content-center gap-2"
                    disabled={isPublishing || ytBusy || !ytConnected}
                    onClick={async () => {
                      if (!ytConnected) {
                        // eslint-disable-next-line no-alert
                        alert(t("pr_youtube_connect_first"));
                        return;
                      }

                      setIsPublishing(true);
                      try {
                        const res = await apiFetchAny('/youtube-upload', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...authHeaders() },
                          body: JSON.stringify({
                            projectId: project.id,
                            title: shareTitle,
                            description: shareDescription
                          })
                        });
                        const data = await safeJson(res);

                        if (!res) throw new Error('YouTube upload failed');
                        if (!res.ok) {
                          if (data?.error === 'YOUTUBE_NOT_CONNECTED') {
                            // eslint-disable-next-line no-alert
                            alert(t("pr_youtube_connect_first"));
                            await refreshYoutubeStatus();
                            return;
                          }
                          throw new Error(data?.error || 'Upload failed');
                        }

                        if (data.video_id) {
                          setYoutubeVideoId(data.video_id);
                          try {
                            await apiFetchJson(`/youtube-stats/${data.video_id}`, { headers: { ...authHeaders() } });
                          } catch {}
                          await loadShareAndStats();
                        }
                      } catch (err) {
                        // eslint-disable-next-line no-alert
                        alert(err?.message || 'Erreur réseau ou serveur');
                      } finally {
                        setIsPublishing(false);
                      }
                    }}
                  >
                    {isPublishing ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>{t("pr_publishing")}</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload"></i>
                        <span>{t("pr_publish")}</span>
                      </>
                    )}
                  </button>

                  <div className="wv-muted small mt-3">
                    {t("pr_publish_tip")}
                  </div>
                </>
              )}

              {(isOwner || isCoWriter) && youtubeVideoId && (
                <>
                  <div className="ratio ratio-16x9 border rounded-4 overflow-hidden mb-3">
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                      title="YouTube video"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.viewCount === 'number' ? youtubeStats.viewCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-eye"></i> {t("pr_views")}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.likeCount === 'number' ? youtubeStats.likeCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-hand-thumbs-up"></i> {t("pr_likes")}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.commentCount === 'number' ? youtubeStats.commentCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-chat-dots"></i> {t("pr_comments_short")}
                        </div>
                      </div>
                    </div>
                  </div>

                  
<div className="wv-rtCard">
  <div className="wv-rtHeader">
    <div className="wv-rtTitle">
      <i className="bi bi-graph-up"></i>
      <span>{t("pr_realtime_views")}</span>
    </div>
  </div>

  <div className="wv-rtGrid">
    <div className="wv-rtChart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={sampleRealtimeData}>
          <XAxis dataKey="time" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Line type="monotone" dataKey="views" stroke="#111827" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>

    <div className="wv-rtSide">
      <div className="wv-rtMini">
        <div className="wv-rtMiniLabel">
          <i className="bi bi-hand-thumbs-up"></i> Likes (demo)
        </div>
        <div className="wv-rtMiniChart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyLikes}>
              <XAxis dataKey="day" hide />
              <YAxis hide />
              <Tooltip />
              <Line type="monotone" dataKey="likes" stroke="#111827" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="wv-rtMini">
        <div className="wv-rtMiniLabel">
          <i className="bi bi-chat-dots"></i> Commentaires (demo)
        </div>
        <div className="wv-rtMiniChart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyComments}>
              <XAxis dataKey="day" hide />
              <YAxis hide />
              <Tooltip />
              <Line type="monotone" dataKey="comments" stroke="#111827" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  </div>
</div>

                  <div className="alert alert-info rounded-4 mt-3 mb-0">
                    <div className="d-flex align-items-start gap-2">
                      <i className="bi bi-currency-euro fs-5"></i>
                      <div>
                        {t("pr_revenue_est")} <span style={{ fontWeight: 600 }}>{estimatedRevenue.toFixed(2)} €</span>
                        <div className="small text-muted">
                          {t("pr_revenue_note")}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="small text-muted fw-semibold text-uppercase mb-2 d-inline-flex align-items-center gap-2">
                      <i className="bi bi-card-text"></i>
                      Détails de publication
                    </div>
                    <div className="border rounded-4 p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                      <div className="mb-1" style={{ fontWeight: 600 }}>{shareTitle || <em className="text-muted">Non renseigné</em>}</div>
                      <div className="small">{shareDescription || <em className="text-muted">Aucune description fournie</em>}</div>
                    </div>
                  </div>
                </>
              )}


              {!(isOwner || isCoWriter) && youtubeVideoId && (
                <>
                  <div className="ratio ratio-16x9 border rounded-4 overflow-hidden mb-3">
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                      title="YouTube video"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.viewCount === 'number' ? youtubeStats.viewCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-eye"></i> Vues
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.likeCount === 'number' ? youtubeStats.likeCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-hand-thumbs-up"></i> Likes
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="bg-light rounded-4 p-3 text-center h-100">
                        <div className="h4 mb-0">{typeof youtubeStats?.commentCount === 'number' ? youtubeStats.commentCount : '—'}</div>
                        <div className="small text-muted mt-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-chat-dots"></i> Com.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-light border rounded-4 mb-0">
                    <div className="d-flex align-items-start gap-2">
                      <i className="bi bi-info-circle"></i>
                      <div className="small">
                        Publication gérée par le responsable du projet. Vous pouvez consulter la vidéo et les statistiques.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!isOwner && !isCoWriter && !youtubeVideoId && (
                <div className="wv-muted">Cette section est réservée aux responsables du projet.</div>
              )}
            </SectionShell>
          )}

          {/* Share */}
          <SectionShell
            id="share"
            icon="bi-share"
            title={t("pr_watch_or_share")}
            subtitle={t("pr_link_qr")}
            right={
              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                onClick={copyLink}
                disabled={!finalVideoSafe}
              >
                <i className="bi bi-clipboard"></i>
                <span className="d-none d-sm-inline">{copied ? 'Copié !' : 'Copier'}</span>
              </button>
            }
          >
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-4 d-flex justify-content-center">
                <div className="text-center">
                  <QRCodeCanvas value={finalVideoSafe || ''} size={140} className="my-2" />
                  <div className="small text-muted d-inline-flex align-items-center gap-2 justify-content-center">
                    <i className="bi bi-qr-code"></i> Scanner pour ouvrir
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-8">
                <div className="d-flex flex-column gap-2">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-link-45deg"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      value={finalVideoSafe || ''}
                      readOnly
                    />
                    <button className="btn btn-outline-primary" type="button" onClick={copyLink} disabled={!finalVideoSafe}>
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <a
                      href={finalVideoSafe}
                      className="btn btn-outline-success rounded-pill d-inline-flex align-items-center gap-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { if (!finalVideoSafe) e.preventDefault(); }}
                    >
                      <i className="bi bi-play-circle"></i> Visionner
                    </a>

                    <a
                      href={finalVideoSafe}
                      download
                      className="btn btn-outline-secondary rounded-pill d-inline-flex align-items-center gap-2"
                      onClick={(e) => { if (!finalVideoSafe) e.preventDefault(); }}
                    >
                      <i className="bi bi-download"></i> Télécharger
                    </a>

                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(finalVideoSafe || '')}&text=Découvrez ce documentaire !`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-dark rounded-pill d-inline-flex align-items-center gap-2"
                      onClick={(e) => { if (!finalVideoSafe) e.preventDefault(); }}
                    >
                      <i className="bi bi-twitter-x"></i> Partager
                    </a>
                  </div>

                  {copied && (
                    <div className="small text-success d-inline-flex align-items-center gap-2">
                      <i className="bi bi-check2"></i> Lien copié
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!isInternal && (
              <div className="alert alert-light border rounded-4 mt-3 mb-0">
                <div className="d-flex align-items-start gap-2">
                  <i className="bi bi-info-circle"></i>
                  <div className="small">
                    Vous pouvez visionner la vidéo et partager le lien. Les détails du projet (scénario, forum, contributions) sont visibles uniquement aux participants.
                  </div>
                </div>
              </div>
            )}
          </SectionShell>

          {/* External-only share card already above */}
          </div>
        )}
      </div>

      <div className="mt-4">

          {/* Scenario */}
          
{isInternal && project?.scenario && (
  <SectionShell
    id="scenario"
    icon="bi-signpost-2"
    title="Étapes du scénario"
    subtitle={steps?.length ? `${steps.length} étape${steps.length > 1 ? 's' : ''}` : 'Détails du scénario'}
    cardClassName="wv-jaCard"
    cardStyle={{ width: '100%', maxWidth: '100%' }}
    right={
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
        onClick={() => setShowScenario((v) => !v)}
      >
        <i className={`bi ${showScenario ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        {showScenario ? t('common_hide') : t('common_show')}
      </button>
    }
  >
    {showScenario ? (
      steps?.length ? (
        <ScenarioCards steps={steps || []} />
      ) : (
        <div className="wv-muted">Aucune étape enregistrée.</div>
      )
    ) : (
      <div className="wv-muted small">Section masquée.</div>
    )}
  </SectionShell>
)}

{/* Commentaires (vidéo finale) */}
{isInternal && (
  <SectionShell
    id="comments"
    icon="bi-chat-dots"
    title="Commentaires"
    subtitle={`${formatCompact(commentCountDisplay)} commentaire${commentCountDisplay > 1 ? 's' : ''}`}
    cardClassName="wv-jaCard"
    cardStyle={{ width: '100%', maxWidth: '100%' }}
    right={
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
        onClick={() => setShowComments((v) => !v)}
      >
        <i className={`bi ${showComments ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        {showComments ? t('common_hide') : t('common_show')}
      </button>
    }
  >
    {toast ? (
      <div className={`wv-toast wv-toast--${toast.type || 'info'}`} role="status" aria-live="polite">
        <i
          className={`bi ${
            toast.type === 'success'
              ? 'bi-check-circle'
              : toast.type === 'error'
              ? 'bi-exclamation-triangle'
              : 'bi-info-circle'
          }`}
        ></i>
        <span className="text-truncate">{toast.message}</span>
      </div>
    ) : null}

    {showComments ? (
      <div className="wv-commentsWrap">
        {getAuthToken() ? (
          <div className="wv-commentComposer">
            <textarea
              className="form-control wv-commentInput"
              rows={2}
              placeholder={t("pr_add_comment_ph")}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
              onClick={submitFinalComment}
              disabled={commentLoading || !String(newComment || '').trim()}
            >
              {commentLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Envoi…</span>
                </>
              ) : (
                <>
                  <i className="bi bi-send"></i>
                  <span>{t("pr_publish")}</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="alert alert-secondary rounded-4 d-flex align-items-start gap-2 mb-3">
            <i className="bi bi-lock mt-1"></i>
            <div className="small">
              Connectez-vous pour publier un commentaire. Vous pouvez tout de même consulter les commentaires existants.
            </div>
          </div>
        )}

        <div className="wv-commentList">
          {Array.isArray(finalComments) && finalComments.length ? (
            finalComments
              .slice()
              .sort((a, b) => new Date(b.createdAt || b.created_at || b.created || 0) - new Date(a.createdAt || a.created_at || a.created || 0))
              .map((c, idx2) => {
                const authorId =
                  c.author ||
                  c.username ||
                  c.user ||
                  c.user_id ||
                  c.created_by ||
                  c.sender ||
                  c.sender_id ||
                  c.original_sender ||
                  '';
                const authorName =
                  c.author_name ||
                  c.authorName ||
                  c.display_name ||
                  c.displayName ||
                  (authorId ? displayNameFor(authorId) : 'Anonyme');

                const text =
                  c.text ||
                  c.comment ||
                  c.message ||
                  c.body ||
                  '';

                const created =
                  c.createdAt || c.created_at || c.created || c.timestamp || null;

                return (
                  <div className="wv-commentItem" key={c.id || c.comment_id || `c-${idx2}`}>
                    <div className="d-flex align-items-start gap-2">
                      <ImgAvatar src={avatarFor(authorId || authorName)} label={authorName} size={34} />
                      <div className="wv-commentBubble flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="wv-commentAuthor text-truncate">{authorName}</div>
                          {created ? (
                            <div className="wv-commentDate">{new Date(created).toLocaleString()}</div>
                          ) : null}
                        </div>
                        <div className="wv-commentText">{text}</div>
                      </div>
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="wv-emptyForum">
              <div className="wv-emptyIcon"><i className="bi bi-chat-dots"></i></div>
              <div className="fw-semibold">{t("pr_no_comment_title")}</div>
              <div className="wv-muted small mt-1">{t("pr_no_comment_desc")}</div>
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="wv-muted small">Cliquez sur « Afficher » pour consulter et ajouter des commentaires.</div>
    )}
  </SectionShell>
)}
          {/* Forum */}
          {isInternal && Array.isArray(projectMessages) && (
            <SectionShell
              id="forum"
              icon="bi-chat-left-text"
              title="Forum"
              subtitle={`${messageCount} message${messageCount > 1 ? 's' : ''}`}
              right={
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                  onClick={() => setShowForum((v) => !v)}
                >
                  <i className={`bi ${showForum ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {showForum ? t('common_hide') : t('common_show')}
                </button>
              }
            >
              {showForum ? (
                <div className="wv-forumWrap">
                  <div className="wv-forumTop">
                    <div className="wv-forumPeople">
                      <div className="wv-avatarStack" aria-label="Participants">
                        {forumPeople.map((name, idx) => (
                          <div
                            key={`${name}-${idx}`}
                            className="wv-avatarStackItem"
                            style={{ zIndex: forumPeople.length - idx }}
                            title={`${displayNameFor(name)} (@${name})`}
                          >
                            <ImgAvatar
                              src={avatarFor(name)}
                              label={displayNameFor(name)}
                              size={34}
                              className="wv-videoContribAvatar"
                            />
                          </div>
                        ))}

                        {forumMoreCount > 0 && (
                          <span className="wv-avatarMore" title={`${forumParticipants.length} participants`}>
                            +{forumMoreCount}
                          </span>
                        )}
                      </div>

                      <div className="wv-forumHint">
                        <i className="bi bi-chat-dots"></i>
                        <span className="text-truncate">
                          Discussion de l'équipe • {forumParticipants.length} participant{forumParticipants.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="wv-forumTools">
                      <div className="wv-forumSearch" role="search" aria-label={t("pr_search_forum")}>
                        <i className="bi bi-search"></i>
                        <input
                          type="text"
                          className="wv-forumSearchInput"
                          placeholder={t("pr_search_forum_ph")}
                          value={forumQuery}
                          onChange={(e) => setForumQuery(e.target.value)}
                        />
                        {forumQuery ? (
                          <button
                            type="button"
                            className="wv-forumClear"
                            onClick={() => setForumQuery('')}
                            aria-label="Effacer la recherche"
                            title="Effacer"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        className="wv-actionPill"
                        onClick={() => scrollForumToBottom(true)}
                        title={t("pr_go_last")}
                        aria-label="Aller aux derniers messages"
                      >
                        <i className="bi bi-arrow-down"></i>
                        <span className="d-none d-md-inline">Derniers</span>
                      </button>
                    </div>
                  </div>

                  <div className="wv-forumList" ref={forumListRef}>
                    {forumMessagesSorted.length ? (
                      forumMessagesSorted.map((msg, idx) => {
                        const rawSender =
                          msg.original_sender ||
                          msg.sender ||
                          msg.username ||
                          msg.user ||
                          msg.user_id ||
                          msg.sender_id ||
                          msg.created_by ||
                          msg.author ||
                          msg.from ||
                          '';
                        const userKey = resolveUserKey(rawSender);
                        const handle = userKey || rawSender || '';
                        const display = displayNameFor(handle);
                        const createdAt = msg.created_at || msg.createdAt || msg.date || '';
                        const isMe = handle && currentUser && String(handle) === String(currentUser);

                        const messageText = msg.message || msg.content || msg.text || msg.body || '';
                        const fileUrlRaw = msg.file_url || msg.fileUrl || msg.file || msg.attachment || msg.file_path || '';
                        const normalizedFile = normalizeUploadsUrl(fileUrlRaw);
                        const isImg = isImagePath(normalizedFile || '');
                        const isVid = isVideoPath(normalizedFile || '');

                        const prev = forumMessagesSorted[idx - 1];
                        const prevCreatedAt = prev?.created_at || prev?.createdAt || prev?.date || null;
                        const showDay =
                          idx === 0 || dayKey(prevCreatedAt) !== dayKey(createdAt);

                        return (
                          <React.Fragment key={msg.id || msg._id || `${handle}-${createdAt}-${idx}`}>
                            {showDay && (
                              <div className="wv-daySep" aria-hidden="true">
                                <span>{formatDayLabel(createdAt)}</span>
                              </div>
                            )}

                            <div className={`wv-bubbleRow ${isMe ? 'is-me' : ''}`}>
                              {!isMe && (
                                <div className="wv-bubbleAvatar" title={`${display} (@${handle})`}>
                                  <ImgAvatar src={avatarFor(handle)} label={display} size={36} />
                                </div>
                              )}

                              <div className={`wv-bubble ${isMe ? 'wv-bubble--me' : ''}`}>
                                <div className="wv-bubbleTop">
                                  <div className="wv-bubbleName">
                                    <span className="text-truncate">{display}</span>
                                    {handle && display !== handle ? (
                                      <span className="wv-bubbleHandle">@{handle}</span>
                                    ) : null}
                                  </div>
                                  <div className="wv-bubbleTime">{formatTimeLabel(createdAt)}</div>
                                </div>

                                {messageText ? (
                                  <div className="wv-bubbleText" style={{ whiteSpace: 'pre-wrap' }}>
                                    {messageText}
                                  </div>
                                ) : null}

                                {normalizedFile ? (
                                  <div className="wv-bubbleAttach">
                                    {isVid ? (
                                      <video
                                        src={normalizedFile}
                                        controls
                                        className="w-100"
                                        style={{ borderRadius: 14, maxHeight: 380, background: '#000' }}
                                        onClick={() =>
                                          setPreview({
                                            url: normalizedFile,
                                            isVideo: true,
                                            title: 'Vidéo',
                                            subtitle: `${display} (@${handle})`,
                                          })
                                        }
                                      />
                                    ) : isImg ? (
                                      <img
                                        src={normalizedFile}
                                        alt="attachment"
                                        className="w-100"
                                        style={{ borderRadius: 14, cursor: 'zoom-in' }}
                                        onClick={() =>
                                          setPreview({
                                            url: normalizedFile,
                                            isVideo: false,
                                            title: 'Image',
                                            subtitle: `${display} (@${handle})`,
                                          })
                                        }
                                      />
                                    ) : (
                                      <a
                                        href={normalizedFile}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                                      >
                                        <i className="bi bi-paperclip"></i>
                                        <span>Ouvrir la pièce jointe</span>
                                      </a>
                                    )}
                                  </div>
                                ) : null}
                              </div>

                              {isMe && (
                                <div className="wv-bubbleAvatar wv-bubbleAvatar--me" title={t("pr_you")}>
                                  <ImgAvatar src={avatarFor(handle)} label={display} size={36} />
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <div className="wv-emptyForum">
                        <div className="wv-emptyIcon"><i className="bi bi-chat-left-text"></i></div>
                        <div className="fw-semibold">Aucun message</div>
                        <div className="wv-muted small mt-1">Les échanges de l'équipe s'afficheront ici.</div>
                      </div>
                    )}
                    <div ref={forumEndRef} />
                  </div>
                </div>
              ) : (
                <div className="wv-muted small">Section masquée.</div>
              )}
            </SectionShell>
          )}

{/* Gallery */}
          {isInternal && (
            <SectionShell
              id="gallery"
              icon="bi-collection"
              title="Galerie des contributions"
              subtitle={t("pr_filter_hint")}
              right={
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                  onClick={() => setShowGallery((v) => !v)}
                >
                  <i className={`bi ${showGallery ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {showGallery ? t('common_hide') : t('common_show')}
                </button>
              }
            >
              {showGallery ? (
                <>
                  <div className="wv-filtersRow mb-3">
                    <div className="wv-filterPills">
                      <button
                        type="button"
                        className={`wv-chip ${galleryType === 'all' ? 'wv-chip--active' : ''}`}
                        onClick={() => setGalleryType('all')}
                      >
                        <i className="bi bi-grid-3x3-gap"></i> Tous
                        <span className="wv-chipValue">{mediaCount}</span>
                      </button>

                      <button
                        type="button"
                        className={`wv-chip ${galleryType === 'images' ? 'wv-chip--active' : ''}`}
                        onClick={() => setGalleryType('images')}
                      >
                        <i className="bi bi-image"></i> Images
                        <span className="wv-chipValue">{imageCount}</span>
                      </button>

                      <button
                        type="button"
                        className={`wv-chip ${galleryType === 'videos' ? 'wv-chip--active' : ''}`}
                        onClick={() => setGalleryType('videos')}
                      >
                        <i className="bi bi-camera-video"></i> Vidéos
                        <span className="wv-chipValue">{videoCount}</span>
                      </button>
                    </div>

                    <div className="wv-filterPills">
                      <button
                        type="button"
                        className={`wv-contribPill ${galleryContributor === 'Tous' ? 'wv-contribPill--active' : ''}`}
                        onClick={() => setGalleryContributor('Tous')}
                        title={t("pr_all_contributors")}
                      >
                        <Avatar name="Tous" size={28} />
                        <span className="small">Tous</span>
                      </button>

                      {contributorNames.slice(0, 10).map((name) => (
                        <button
                          type="button"
                          key={name}
                          className={`wv-contribPill ${galleryContributor === name ? 'wv-contribPill--active' : ''}`}
                          onClick={() => setGalleryContributor(name)}
                          title={`${displayNameFor(name)} (@${name})`}
                        >
                          <ImgAvatar src={avatarFor(name)} label={displayNameFor(name)} size={28} />
                          <span className="small text-truncate" style={{ maxWidth: 140 }}>{displayNameFor(name)}</span>
                          <span className="wv-chipValue">{groupedByContributor[name]?.length || 0}</span>
                        </button>
                      ))}

                      {contributorNames.length > 10 && (
                        <span className="small text-muted ms-1">+{contributorNames.length - 10}</span>
                      )}
                    </div>
                  </div>

                  {galleryContributor !== 'Tous' && (
                    <div className="alert alert-light border rounded-4 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <ImgAvatar src={avatarFor(galleryContributor)} label={displayNameFor(galleryContributor)} size={40} />
                        <div className="overflow-hidden">
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="fw-semibold text-truncate" style={{ fontWeight: 600 }}>
                              {displayNameFor(galleryContributor)}
                            </div>
                            {galleryContributor && displayNameFor(galleryContributor) !== galleryContributor && (
                              <span className="badge bg-light text-dark border rounded-pill">
                                @{galleryContributor}
                              </span>
                            )}
                          </div>
                          <div className="small text-muted">
                            Merci pour la contribution • {groupedByContributor[galleryContributor]?.length || 0} média{(groupedByContributor[galleryContributor]?.length || 0) > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="wv-masonry">
                    {filteredMedia.map((item) => {
                      const mediaUrl = `/uploads/${item.file_path}`;
                      const isVid = isVideoPath(item.file_path || '');
                      const title = item.title || (isVid ? 'Vidéo' : 'Image');

                      return (
                        <div key={`${item.id}-${item.file_path}`} className="wv-masonryItem">
                          <button
                            type="button"
                            className="wv-mediaBtn"
                            onClick={() => setPreview({
                              url: mediaUrl,
                              isVideo: isVid,
                              title,
                              subtitle: displayNameFor(item.__contributor)
                            })}
                            aria-label={`Ouvrir ${title}`}
                          >
                            <div className="wv-mediaCard position-relative">
                              {isVid ? (
                                <LazyVideoThumb src={mediaUrl} />
                              ) : (
                                <div className="wv-mediaThumb">
                                  <img src={mediaUrl} alt={title} loading="lazy" />
                                </div>
                              )}

                              <div className="wv-mediaOverlay">
                                <span className="badge text-bg-dark d-inline-flex align-items-center gap-2">
                                  <i className={`bi ${isVid ? 'bi-camera-video' : 'bi-image'}`}></i>
                                  <span className="text-truncate" style={{ maxWidth: 220 }}>{title}</span>
                                </span>

                                <span className="badge text-bg-light text-dark d-inline-flex align-items-center gap-2">
                                  <i className="bi bi-arrows-angle-expand"></i>
                                  <span className="d-none d-sm-inline">Agrandir</span>
                                </span>
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {!filteredMedia.length && (
                    <div className="wv-muted mt-3">Aucun média pour ce filtre.</div>
                  )}
                </>
              ) : (
                <div className="wv-muted small">Section masquée.</div>
              )}
            </SectionShell>
          )}

      </div>

      {/* Preview modal */}
      {preview && (
        <div
          className="wv-modalBackdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div className="wv-modalCard position-relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn btn-light btn-sm wv-modalClose"
              onClick={() => setPreview(null)}
              aria-label="Fermer"
              title="Fermer"
            >
              <i className="bi bi-x-lg"></i>
            </button>

            <div className="wv-modalMedia">
              {preview.isVideo ? (
                <video src={preview.url} controls />
              ) : (
                <img src={preview.url} alt={preview.title || 'Media'} />
              )}
            </div>

            <div className="wv-modalFooter">
              <div className="flex-grow-1 overflow-hidden">
                <div style={{ fontWeight: 600 }} className="text-truncate">{preview.title || 'Media'}</div>
                {preview.subtitle && <div className="small text-muted text-truncate">{preview.subtitle}</div>}
              </div>

              <div className="d-flex gap-2 flex-shrink-0">
                <a
                  className="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  <span className="d-none d-sm-inline">{t("pr_open")}</span>
                </a>

                <a
                  className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                  href={preview.url}
                  download
                >
                  <i className="bi bi-download"></i>
                  <span className="d-none d-sm-inline">{t("pr_download")}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center mt-4">
        <hr />
        <p className="text-muted small mb-2">Merci à tous les participants pour leur contribution précieuse.</p>
        <a href="/public" className="btn btn-outline-primary rounded-pill d-inline-flex align-items-center gap-2">
          <i className="bi bi-arrow-left"></i>
          Retour au tableau de bord
        </a>
      </div>
    </div>
  );
};

export default ProjectRecap;
